import { EventEditionDetails, ICMSApiResponse } from "../../types";
import store from "../../../redux/store";
import EventHelper from "../../network/helpers/event/EventHelper";
import localForage from "localforage";
import { CMS_EVENT_DETAILS_STORAGE_KEY } from "../../constants";

export type CmsImageOptions = {
  useSharedLogo?: boolean;
  useSharedImages?: boolean;
};

class CMSHelper {
  private eventRequests = new Map<string, Promise<ICMSApiResponse | null>>();

  getCachedCmsEventDetails = async (eventEdition: EventEditionDetails): Promise<ICMSApiResponse | null> => {
    const state = store.getState().eventEdition;
    if (!eventEdition.gbsCode || state.eventEdition?.gbsCode === eventEdition.gbsCode) {
      return state.cmsEventDetails as ICMSApiResponse | null;
    }

    const key = `${eventEdition.gbsCode}:${eventEdition.domain}`;
    const pending = this.eventRequests.get(key);
    if (pending) return pending;

    const request = this.readCmsEventDetails(eventEdition);
    this.eventRequests.set(key, request);
    try {
      return await request;
    } finally {
      this.eventRequests.delete(key);
    }
  };

  private readCmsEventDetails = async (eventEdition: EventEditionDetails): Promise<ICMSApiResponse | null> => {
    let cached: Record<string, ICMSApiResponse> = {};
    try {
      cached = (await localForage.getItem<Record<string, ICMSApiResponse>>(CMS_EVENT_DETAILS_STORAGE_KEY)) || {};
      if (cached[eventEdition.gbsCode]) return cached[eventEdition.gbsCode] || null;
    } catch (e) {
      console.warn("[colocated] unable to read cms event cache", e);
    }

    const response = await EventHelper.getBadgeLocation(eventEdition);
    try {
      const latest = (await localForage.getItem<Record<string, ICMSApiResponse>>(CMS_EVENT_DETAILS_STORAGE_KEY)) || cached;
      await localForage.setItem(CMS_EVENT_DETAILS_STORAGE_KEY, { ...latest, [eventEdition.gbsCode]: response.fullResponse });
    } catch (e) {
      console.warn("[colocated] unable to write cms event cache", e);
    }
    return (response.fullResponse as ICMSApiResponse) || null;
  };

  getPortalImageUrl = (logoUrl: string, baseUrl: string, imageName: string, sku: string) => {
    const NUMBER_OF_DIRECTORIES = 5;
    const splitLogoUrl = (logoUrl || "").split("/");
    const rmIndex = splitLogoUrl.indexOf("content");
    return `${baseUrl}/${splitLogoUrl
      .slice(rmIndex, rmIndex + NUMBER_OF_DIRECTORIES)
      .join("/")}/badge/${sku.toLowerCase()}/${imageName}.jpg/_jcr_content/renditions/original./123/${imageName}.jpg`;
  };

  getPortalSharedImageUrl = (logoUrl: string, baseUrl: string, imageName: string) => {
    const NUMBER_OF_DIRECTORIES = 5;
    const splitLogoUrl = (logoUrl || "").split("/");
    const rmIndex = splitLogoUrl.indexOf("content");
    return `${baseUrl}/${splitLogoUrl
      .slice(rmIndex, rmIndex + NUMBER_OF_DIRECTORIES)
      .join("/")}/badge/default/${imageName}.jpg/_jcr_content/renditions/original./123/${imageName}.jpg`;
  };

  getPortalSharedLogoUrl = (logoUrl: string, baseUrl: string) => this.getPortalSharedImageUrl(logoUrl, baseUrl, "logo");

  getCmsImageUrl = (
    productSku: string,
    imageName: string,
    eventEdition: EventEditionDetails,
    badgeLocation: ICMSApiResponse,
    options?: CmsImageOptions
  ) => {
    const eventLogoUrl = badgeLocation.siteDetails.logo.url;
    const domain = new URL(eventEdition.domain || "").origin;

    if ((options?.useSharedImages || options?.useSharedLogo) && imageName === "logo") {
      return this.getPortalSharedLogoUrl(eventLogoUrl, domain);
    }
    if (options?.useSharedImages) {
      return this.getPortalSharedImageUrl(eventLogoUrl, domain, imageName);
    }

    return this.getPortalImageUrl(eventLogoUrl, domain, imageName, productSku);
  };

  getReceiptLogoUrl = async (locale: string) => {
    const cmsEventDetails = store.getState().eventEdition.cmsEventDetails;
    const logoPath = cmsEventDetails?.siteDetails?.logo.url;
    const domain = new URL(EventHelper.eventEdition?.domain || "").origin;

    const NUMBER_OF_DIRECTORIES = 5;
    const splitLogoUrl = (logoPath || "").split("/");
    const rmIndex = splitLogoUrl.indexOf("content");
    return `${domain}/${splitLogoUrl
      .slice(rmIndex, rmIndex + NUMBER_OF_DIRECTORIES)
      .join("/")}/receipts/${locale.toLowerCase()}.jpg/_jcr_content/renditions/original./123/${locale.toLowerCase()}.jpg`;
  };
}

export default new CMSHelper();
